/** Figma SummaryBox 5519:20152 — sticky mobile summary (offer, course, booking). */
import { attachBottomSheetSwipe } from './bottomSheetSwipe.js';

const ui = (msgid) => {
  const text = String(msgid ?? '');
  if (!text) return '';
  const bag = typeof window !== 'undefined' ? window.hskiI18n?.msg : null;
  const value = bag && typeof bag[text] === 'string' ? bag[text] : '';
  return value || text;
};

export const STICKY_SUMMARY_MQ = '(max-width: 1023px)';

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * @param {object} options
 * @param {HTMLElement} options.root
 * @param {HTMLElement} options.sidebar
 * @param {() => (HTMLElement|null|undefined)} [options.getSidebar] live aside (paczka hero może podmienić węzeł)
 * @param {() => string} options.getTotalText
 * @param {() => { href?: string, text: string, hidden?: boolean, isButton?: boolean }} options.getCta
 * @param {(event: MouseEvent) => void} [options.onCtaClick]
 * @param {string} [options.drawerLabel]
 * @param {string} [options.bodyClass]
 * @param {string} [options.pageClass]
 * @param {string} [options.rootIdKey] dataset key on root
 * @param {string} [options.totalLabel]
 * @param {string} [options.detailsLabel]
 * @param {() => boolean} [options.isHidden]
 * @param {'default' | 'lead' | 'course-pick'} [options.variant] course-pick = belka eSky (edycja + cena + CTA)
 * @param {() => { title?: string, note?: string }} [options.getLeadSummary]
 * @param {() => string} [options.getPriceCaption] podpis przy cenie (np. Od / Razem / za osobę)
 * @param {boolean} [options.autoOpenAtBottom] domyślnie true; lead zwykle false
 * @param {boolean} [options.keepBadgesInDrawer] true = tagi zostają w sheet (nowy design kursów)
 * @param {boolean} [options.keepSidebarInline] true = aside w treści na mobile; false = zawsze w drawerze
 * @param {'clone' | 'relocate'} [options.drawerSidebarMode] clone = kopia w sheet (kursy); relocate = prawdziwy aside na czas otwarcia (oferty)
 * @param {string} [options.inlineMountAfter] selektor w root — na mobile inline aside ląduje zaraz po tym elemencie (oferty: galeria)
 */
export const initStickySummaryBar = ({
  root,
  sidebar,
  getSidebar,
  getTotalText,
  getCta,
  onCtaClick,
  onBeforeClose,
  isHidden,
  getLeadSummary,
  getPriceCaption,
  variant = 'default',
  autoOpenAtBottom = variant !== 'lead',
  keepBadgesInDrawer = false,
  keepSidebarInline = variant === 'course-pick',
  drawerSidebarMode = 'clone',
  inlineMountAfter = '',
  drawerLabel = 'Szczegóły rezerwacji',
  totalLabel = 'Razem do zapłaty',
  detailsLabel = 'Szczegóły',
  bodyClass = 'has-sticky-summary-bar',
  pageClass = 'has-sticky-summary',
  rootIdKey = 'offerSidebarMobileId',
}) => {
  if (!root || !sidebar) return { sync() {}, closeDrawer() {}, openDrawer() {} };

  const mq = window.matchMedia(STICKY_SUMMARY_MQ);
  const isLeadVariant = variant === 'lead';
  const isCoursePickVariant = variant === 'course-pick';
  const useInlineSidebar = Boolean(keepSidebarInline);
  const useRelocateDrawer = useInlineSidebar && drawerSidebarMode === 'relocate';
  const useCloneDrawer = useInlineSidebar && !useRelocateDrawer;
  let bar = null;
  let drawer = null;
  let drawerBody = null;
  /** Oryginalna kolumna desktop (np. .offer-page__sidebar-col). */
  let desktopSlot = sidebar.parentElement;
  let sidebarSlot = desktopSlot;
  /** Live aside — paczka `hero` na Pingwinie podmienia węzeł przy zmianie stoku. */
  let sidebarRef = sidebar;
  const resolveSidebar = () => {
    if (typeof getSidebar === 'function') {
      const live = getSidebar();
      if (live instanceof HTMLElement) {
        if (live !== sidebarRef) {
          sidebarRef = live;
          if (!useInlineSidebar || !mq.matches) {
            desktopSlot = live.parentElement || desktopSlot;
          }
          sidebarSlot = live.parentElement || sidebarSlot || desktopSlot;
        }
        return live;
      }
    }
    return sidebarRef;
  };
  /** @type {HTMLElement | null} */
  let inlineSlot = null;
  let moved = false;
  /** @type {HTMLElement | null} */
  let heroBadges = null;
  let detachSwipe = null;
  let detachBottomWatch = null;
  let bottomDismissed = false;
  let bottomScrollTick = 0;
  /** @type {HTMLElement | null} */
  let coursePickClone = null;
  let coursePickProxyBound = false;
  /** Wizualny placeholder w treści gdy prawdziwy aside jest w drawerze (relocate). */
  /** @type {HTMLElement | null} */
  let inlinePlaceholder = null;
  /** @type {(() => void) | null} */
  let detachVisualViewport = null;

  const syncBarToVisualViewport = () => {
    if (!bar || bar.hidden) return;
    const vv = window.visualViewport;
    if (!vv) {
      bar.style.bottom = '0px';
      return;
    }
    // Odstęp między dołem layout viewport a dołem visual viewport (klawiatura / pasek Safari).
    const inset = Math.max(0, window.innerHeight - vv.offsetTop - vv.height);
    bar.style.bottom = `${inset}px`;
  };

  const attachVisualViewportSync = () => {
    if (detachVisualViewport) return;
    const onChange = () => syncBarToVisualViewport();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', onChange, { passive: true });
    vv?.addEventListener('scroll', onChange, { passive: true });
    window.addEventListener('resize', onChange, { passive: true });
    detachVisualViewport = () => {
      vv?.removeEventListener('resize', onChange);
      vv?.removeEventListener('scroll', onChange);
      window.removeEventListener('resize', onChange);
      detachVisualViewport = null;
    };
    syncBarToVisualViewport();
  };

  const detachVisualViewportSync = () => {
    detachVisualViewport?.();
    detachVisualViewport = null;
    if (bar) bar.style.bottom = '';
  };

  const ensureShell = () => {
    const rootIdExisting = root.dataset[rootIdKey];
    if (root.dataset.stickySummaryReady === '1' && rootIdExisting) {
      bar = document.body.querySelector(`.booking-mobile-bar[data-summary-root-id="${rootIdExisting}"]`);
      drawer = document.body.querySelector(`.offer-page__sidebar-drawer[data-summary-root-id="${rootIdExisting}"]`);
      drawerBody = drawer?.querySelector('.js-offer-sidebar-drawer-body');
      const hasEsky = Boolean(bar?.querySelector('.booking-mobile-bar__esky'));
      const wantsEsky = isCoursePickVariant;
      // Stara belka (default/lead) po deployu eSky — przebuduj markup.
      if (bar && wantsEsky !== hasEsky) {
        bar.remove();
        drawer?.remove();
        bar = null;
        drawer = null;
        drawerBody = null;
        root.dataset.stickySummaryReady = '0';
      } else if (bar) {
        bar.classList.toggle('booking-mobile-bar--course-pick', isCoursePickVariant);
        bar.classList.toggle('booking-mobile-bar--lead', isLeadVariant);
        return;
      } else {
        // Flaga ready, ale belka zniknęła z DOM — zbuduj od nowa.
        root.dataset.stickySummaryReady = '0';
      }
    }

    const rootId = root.dataset.offerId || String(Math.random()).slice(2);

    bar = document.createElement('div');
    bar.className = [
      'booking-mobile-bar',
      isLeadVariant ? 'booking-mobile-bar--lead' : '',
      isCoursePickVariant ? 'booking-mobile-bar--course-pick' : '',
    ].filter(Boolean).join(' ');
    bar.dataset.summaryRootId = rootId;
    bar.dataset.offerRootId = rootId;
    bar.hidden = true;
    bar.innerHTML = isCoursePickVariant
      ? `
      <div class="booking-mobile-bar__esky">
        <button type="button" class="booking-mobile-bar__edit js-sticky-summary-open" aria-label="Szczegóły rezerwacji">
          <svg class="booking-mobile-bar__edit-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 20l.9-4.1L14.15 6.65l3.2 3.2L7.1 19.1 4 20Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
            <path d="M14.15 6.65 15.4 5.4a1.8 1.8 0 0 1 2.5 0l.7.7a1.8 1.8 0 0 1 0 2.5L17.35 9.85 14.15 6.65Z" fill="currentColor"/>
          </svg>
        </button>
        <div class="booking-mobile-bar__price-inline">
          <span class="booking-mobile-bar__price-value js-sticky-summary-total">—</span>
          <span class="booking-mobile-bar__price-caption js-sticky-summary-od">Od</span>
        </div>
        <a href="#" class="booking-mobile-bar__cta-pill js-sticky-summary-cta">${ui('Wybierz pakiet')}</a>
      </div>
    `
      : `
      <div class="booking-mobile-bar__summary">
        <div class="booking-mobile-bar__total">
          <span class="booking-mobile-bar__total-label">${totalLabel}</span>
          <span class="booking-mobile-bar__total-value js-sticky-summary-total">—</span>
        </div>
        <div class="booking-mobile-bar__lead" hidden>
          <span class="booking-mobile-bar__lead-title js-sticky-summary-lead-title"></span>
          <span class="booking-mobile-bar__lead-note js-sticky-summary-lead-note"></span>
        </div>
        <button type="button" class="booking-mobile-bar__details js-sticky-summary-open">${detailsLabel}</button>
      </div>
      <div class="booking-mobile-bar__cta">
        <a href="#" class="btn btn--primary btn--block btn--no-arrow js-sticky-summary-cta">Przejdź dalej</a>
      </div>
    `;

    drawer = document.createElement('div');
    drawer.className = [
      'offer-page__sidebar-drawer',
      isLeadVariant ? 'offer-page__sidebar-drawer--lead' : '',
      isCoursePickVariant ? 'offer-page__sidebar-drawer--course-pick' : '',
    ].filter(Boolean).join(' ');
    drawer.dataset.summaryRootId = rootId;
    drawer.dataset.offerRootId = rootId;
    drawer.hidden = true;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.innerHTML = `
      <button type="button" class="offer-page__sidebar-drawer-backdrop js-sticky-summary-close" tabindex="-1" aria-label="Zamknij"></button>
      <div class="offer-page__sidebar-drawer-panel" role="dialog" aria-modal="true" aria-label="${drawerLabel}">
        <div class="offer-page__sidebar-drawer-header" data-bottom-sheet-handle>
          <div class="offer-page__sidebar-drawer-handle js-bottom-sheet-handle" aria-hidden="true"></div>
          <button type="button" class="offer-page__sidebar-drawer-close js-sticky-summary-close" aria-label="Zamknij"></button>
        </div>
        <div class="offer-page__sidebar-drawer-body js-offer-sidebar-drawer-body"></div>
      </div>
    `;

    document.body.appendChild(bar);
    document.body.appendChild(drawer);
    root.dataset[rootIdKey] = rootId;
    root.dataset.stickySummaryReady = '1';
    drawerBody = drawer.querySelector('.js-offer-sidebar-drawer-body');

    bar.querySelector('.js-sticky-summary-open')?.addEventListener('click', () => openDrawer());
    // Sheet otwiera tylko przycisk „Szczegóły” / pastylka ceny — bez swipe z belki i bez tapu w cenę.
    bar.querySelector('.js-sticky-summary-cta')?.addEventListener('click', (event) => {
      if (onCtaClick) onCtaClick(event);
    });
    drawer.querySelectorAll('.js-sticky-summary-close').forEach((el) => {
      el.addEventListener('click', () => closeDrawer());
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && document.body.classList.contains('is-offer-sidebar-drawer-open')) {
        closeDrawer();
      }
    });
    document.addEventListener('hski:close-offer-drawers', () => {
      closeDrawer({ immediate: true });
    });

    detachSwipe?.();
    // peek: null — belka nie otwiera sheetu gestem; zamknięcie swipe w otwartym drawerze zostaje.
    detachSwipe = attachBottomSheetSwipe({
      peek: null,
      drawer,
      panel: drawer.querySelector('.offer-page__sidebar-drawer-panel'),
      backdrop: drawer.querySelector('.offer-page__sidebar-drawer-backdrop'),
      scrollEl: drawerBody,
      open: () => openDrawer(),
      close: () => closeDrawer(),
      isOpen: () => Boolean(drawer?.classList.contains('is-open')),
    });
  };

  const ensureInlineMountSlot = () => {
    if (!inlineMountAfter || inlineSlot?.isConnected) return inlineSlot;
    const anchor = root.querySelector(inlineMountAfter);
    if (!(anchor instanceof HTMLElement)) return null;
    inlineSlot = document.createElement('div');
    inlineSlot.className = 'js-sticky-summary-inline-slot offer-page__sidebar-inline-slot';
    anchor.insertAdjacentElement('afterend', inlineSlot);
    return inlineSlot;
  };

  const activeInlineSlot = () => {
    if (!useInlineSidebar || !mq.matches) return null;
    return ensureInlineMountSlot();
  };

  const moveSidebarToDrawer = () => {
    const sidebar = resolveSidebar();
    if (moved || !drawerBody || !sidebar) return;
    if (!sidebarSlot) sidebarSlot = sidebar.parentElement || desktopSlot;
    // Stary Figma: tagi w hero. Nowy design kursów: tagi w sheet.
    if (!keepBadgesInDrawer) {
      const badges = sidebar.querySelector(
        '.school-product-page__badges--sidebar, .school-product-page__badges--hero'
      );
      if (badges && sidebarSlot) {
        heroBadges = badges;
        sidebarSlot.insertBefore(badges, sidebar);
      }
    }
    // Oferta: karta pod galerią ma zostać widoczna — nieinteraktywny klon w slocie,
    // prawdziwy aside idzie do sheetu (goście / terminy bez proxy).
    if (useRelocateDrawer && useInlineSidebar && sidebarSlot && !inlinePlaceholder) {
      const placeholder = sidebar.cloneNode(true);
      placeholder.classList.add('js-sticky-summary-inline-placeholder');
      // Drop JS hooks so Offer.js never reads/writes this visual clone.
      placeholder.classList.remove('js-offer-sidebar');
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      placeholder.querySelectorAll('button, a, input, select, textarea').forEach((el) => {
        el.setAttribute('tabindex', '-1');
        if (el instanceof HTMLAnchorElement) {
          el.removeAttribute('href');
          el.setAttribute('role', 'presentation');
        }
        if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
          el.disabled = true;
        }
      });
      sidebarSlot.insertBefore(placeholder, sidebar);
      inlinePlaceholder = placeholder;
    }
    drawerBody.appendChild(sidebar);
    moved = true;
  };

  const restoreSidebarInline = () => {
    const sidebar = resolveSidebar();
    const slot = activeInlineSlot() || desktopSlot || sidebarSlot;
    if (!slot || !sidebar) return;
    if (inlinePlaceholder) {
      inlinePlaceholder.remove();
      inlinePlaceholder = null;
    }
    if (heroBadges && !sidebar.contains(heroBadges)) {
      sidebar.insertBefore(heroBadges, sidebar.firstChild);
    }
    if (sidebar.parentElement !== slot) {
      slot.appendChild(sidebar);
    }
    sidebarSlot = slot;
    heroBadges = null;
    moved = false;
  };

  const clearCoursePickDrawerClone = () => {
    if (!drawerBody) return;
    drawerBody.innerHTML = '';
    coursePickClone = null;
  };

  const prepareCoursePickClone = (clone) => {
    clone.classList.add('js-course-pick-drawer-clone');
    clone.querySelectorAll('[id]').forEach((el) => {
      el.id = `drawer-clone-${el.id}`;
    });
    clone.querySelectorAll('label[for]').forEach((el) => {
      const forId = el.getAttribute('for');
      if (forId) el.setAttribute('for', `drawer-clone-${forId}`);
    });
    clone.querySelectorAll('input[name]').forEach((el) => {
      el.name = `drawer-clone-${el.name}`;
    });
    clone.querySelectorAll('.js-school-combo').forEach((wrap) => {
      delete wrap.dataset.comboBound;
    });
    return clone;
  };

  const syncCoursePickDrawerClone = () => {
    const sidebar = resolveSidebar();
    if (!useCloneDrawer || !drawerBody || !sidebar) return;
    const clone = prepareCoursePickClone(sidebar.cloneNode(true));
    drawerBody.replaceChildren(clone);
    coursePickClone = clone;
  };

  const bindCoursePickDrawerProxy = () => {
    if (!drawerBody || coursePickProxyBound) return;
    coursePickProxyBound = true;

    const isTermPickerCombo = (wrap) => Boolean(
      wrap
      && (wrap.classList.contains('js-school-course-term') || wrap.dataset.termPicker === '1')
    );

    /** Clone nie ma listenerów SchoolProduct — otwórz prawdziwy modal terminu (jak lead form). */
    const openOriginalTermPicker = (cloneWrap) => {
      const sidebar = resolveSidebar();
      if (!sidebar) return;
      const combo = cloneWrap?.dataset?.combo || '';
      const origWrap = combo
        ? sidebar.querySelector(`.js-school-combo[data-combo="${CSS.escape(combo)}"]`)
        : (
          sidebar.querySelector('.js-school-combo.js-school-course-term')
          || sidebar.querySelector('.js-school-combo[data-term-picker="1"]')
        );
      if (!origWrap) return;
      closeDrawer({ immediate: true });
      window.requestAnimationFrame(() => {
        origWrap.dispatchEvent(new CustomEvent('school-term-picker:open', { bubbles: true }));
      });
    };

    drawerBody.addEventListener('click', (event) => {
      if (!useCloneDrawer || !coursePickClone) return;
      if (!coursePickClone.contains(event.target)) return;
      const sidebar = resolveSidebar();
      if (!sidebar) return;

      const changeTermBtn = event.target.closest?.('.js-school-change-term');
      if (changeTermBtn) {
        event.preventDefault();
        event.stopPropagation();
        const wrap = changeTermBtn.closest('.js-school-combo')
          || coursePickClone.querySelector('.js-school-combo.js-school-course-term')
          || coursePickClone.querySelector('.js-school-combo[data-term-picker="1"]');
        openOriginalTermPicker(wrap);
        return;
      }

      const sportOption = event.target.closest?.('.school-product-page__sport-option');
      if (sportOption) {
        event.preventDefault();
        const discipline = sportOption.dataset.discipline
          || sportOption.querySelector('input[type="radio"]')?.value
          || '';
        const orig = discipline
          ? (
            sidebar.querySelector(`.school-product-page__sport-option[data-discipline="${CSS.escape(discipline)}"] input`)
            || sidebar.querySelector(`.school-product-page__sport-option input[value="${CSS.escape(discipline)}"]`)
          )
          : null;
        if (orig) {
          orig.click();
          window.requestAnimationFrame(() => syncCoursePickDrawerClone());
        }
        return;
      }

      const trigger = event.target.closest?.('.js-school-combo-trigger');
      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        const wrap = trigger.closest('.js-school-combo');
        if (isTermPickerCombo(wrap)) {
          openOriginalTermPicker(wrap);
          return;
        }
        const list = wrap?.querySelector('.js-school-combo-list');
        if (!wrap || !list) return;
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        coursePickClone.querySelectorAll('.js-school-combo').forEach((other) => {
          if (other === wrap) return;
          other.querySelector('.js-school-combo-trigger')?.setAttribute('aria-expanded', 'false');
          const otherList = other.querySelector('.js-school-combo-list');
          if (otherList) otherList.hidden = true;
        });
        trigger.setAttribute('aria-expanded', String(!isOpen));
        list.hidden = isOpen;
        return;
      }

      const option = event.target.closest?.('.js-school-combo-option');
      if (option) {
        event.preventDefault();
        event.stopPropagation();
        const wrap = option.closest('.js-school-combo');
        const combo = wrap?.dataset.combo || '';
        const value = option.dataset.value || '';
        const origWrap = combo
          ? sidebar.querySelector(`.js-school-combo[data-combo="${CSS.escape(combo)}"]`)
          : null;
        const origOpt = origWrap
          ? [...origWrap.querySelectorAll('.js-school-combo-option')].find((opt) => opt.dataset.value === value)
          : null;
        if (origOpt) {
          origOpt.click();
          window.requestAnimationFrame(() => syncCoursePickDrawerClone());
        }
        return;
      }

      const cta = event.target.closest?.('.js-school-course-cta, .js-school-lesson-cta, .js-instructor-lead-open');
      if (cta) {
        event.preventDefault();
        const selector = [...cta.classList]
          .filter((name) => name.startsWith('js-'))
          .map((name) => `.${name}`)
          .join('');
        const origCta = selector ? sidebar.querySelector(selector) : null;
        if (origCta) origCta.click();
      }
    });
  };

  const barShouldHide = () => {
    if (typeof isHidden === 'function') return Boolean(isHidden());
    return root.classList.contains('booking-page--travel-success')
      || root.classList.contains('booking-page--lesson-confirm');
  };

  const sync = () => {
    if (!bar) return;
    if (barShouldHide()) {
      bar.hidden = true;
      closeDrawer({ immediate: true });
      return;
    }
    if (mq.matches) bar.hidden = false;

    const totalEl = bar.querySelector('.js-sticky-summary-total');
    const totalWrap = bar.querySelector('.booking-mobile-bar__total');
    const totalLabelEl = bar.querySelector('.booking-mobile-bar__total-label');
    const leadWrap = bar.querySelector('.booking-mobile-bar__lead');
    const leadTitleEl = bar.querySelector('.js-sticky-summary-lead-title');
    const leadNoteEl = bar.querySelector('.js-sticky-summary-lead-note');
    const totalText = isLeadVariant ? '' : (getTotalText?.() || '').trim();
    const lead = isLeadVariant || !totalText
      ? (getLeadSummary?.() || {})
      : null;
    const showLead = Boolean(lead && (lead.title || lead.note) && (isLeadVariant || !totalText));

    if (totalEl) totalEl.textContent = totalText || '—';
    if (totalWrap) totalWrap.hidden = showLead || !totalText;
    if (leadWrap) leadWrap.hidden = !showLead;
    if (leadTitleEl) {
      leadTitleEl.textContent = lead?.title || '';
      leadTitleEl.hidden = !lead?.title;
    }
    if (leadNoteEl) {
      leadNoteEl.textContent = lead?.note || '';
      leadNoteEl.hidden = !lead?.note;
    }
    bar.classList.toggle('booking-mobile-bar--lead', showLead);

    const priceCaption = typeof getPriceCaption === 'function'
      ? String(getPriceCaption() ?? '').trim()
      : '';
    // eSky: lokalizacja/tytuł (lead) vs kwota — inna typografia.
    // Kwota z etykietą Od/Razem zawsze jak na Pingwinie (duża), nawet bez „zł” w stringu.
    const captionLooksLikePrice = /^(od|razem)$/i.test(priceCaption || '');
    const looksLikeMoney = captionLooksLikePrice
      || (/\d/.test(totalText) && /(zł|pln|€|\$)/i.test(totalText));
    bar.classList.toggle('is-lead-copy', isCoursePickVariant && Boolean(totalText) && !looksLikeMoney);

    if (totalLabelEl && priceCaption) {
      totalLabelEl.textContent = /^razem$/i.test(priceCaption)
        ? (totalLabel || 'Razem do zapłaty')
        : priceCaption;
    }

    const odEl = bar.querySelector('.js-sticky-summary-od');
    if (odEl) {
      const caption = priceCaption || 'Od';
      odEl.textContent = caption || 'Od';
      odEl.hidden = !caption;
    }

    const ctaEl = bar.querySelector('.js-sticky-summary-cta');
    const cta = getCta?.() || {};
    if (!ctaEl) return;
    ctaEl.textContent = cta.text || (isCoursePickVariant ? ui('Wybierz pakiet') : ui('Przejdź dalej'));
    ctaEl.hidden = Boolean(cta.hidden);
    ctaEl.classList.toggle('is-muted', Boolean(cta.muted));
    ctaEl.classList.toggle('is-pick-package', Boolean(cta.pickPackage));
    if (cta.isButton) {
      ctaEl.removeAttribute('href');
      ctaEl.setAttribute('role', 'button');
    } else {
      ctaEl.setAttribute('href', cta.href || '#');
      ctaEl.removeAttribute('role');
    }
  };

  const atPageBottom = () => {
    const view = window.innerHeight;
    const maxScroll = document.documentElement.scrollHeight - view;
    if (maxScroll < 160) return false;
    return window.scrollY >= maxScroll - 72;
  };

  const atOfferMid = () => {
    const view = window.innerHeight;
    const maxScroll = document.documentElement.scrollHeight - view;
    if (maxScroll <= 0) return true;
    return window.scrollY <= maxScroll * 0.5;
  };

  const isDrawerOpen = () => Boolean(drawer && !drawer.hidden && drawer.classList.contains('is-open'));

  const openDrawer = ({ auto = false } = {}) => {
    if (!drawer) return;
    if (document.body.classList.contains('mobile-menu-open')) return;
    if (auto && isDrawerOpen()) return;
    if (useCloneDrawer) {
      syncCoursePickDrawerClone();
      bindCoursePickDrawerProxy();
    } else if (useRelocateDrawer) {
      moveSidebarToDrawer();
    }
    const panel = drawer.querySelector('.offer-page__sidebar-drawer-panel');
    const alreadyVisible = !drawer.hidden || panel?.classList.contains('is-dragging');
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-offer-sidebar-drawer-open', 'overflow-hidden');
    root.classList.add('is-sidebar-drawer-open');
    const show = () => drawer.classList.add('is-open');
    if (alreadyVisible || prefersReducedMotion()) {
      show();
    } else {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(show);
      });
    }
    if (!auto) {
      drawer.querySelector('.offer-page__sidebar-drawer-close')?.focus();
    }
  };

  const checkPageBottom = () => {
    if (!autoOpenAtBottom || !mq.matches || !drawer) return;
    if (document.body.classList.contains('mobile-menu-open')) return;
    if (bottomDismissed) {
      if (!atOfferMid()) return;
      bottomDismissed = false;
    }
    if (!atPageBottom() || isDrawerOpen()) return;
    bottomDismissed = true;
    openDrawer({ auto: true });
  };

  const onPageScroll = () => {
    if (bottomScrollTick) return;
    bottomScrollTick = window.requestAnimationFrame(() => {
      bottomScrollTick = 0;
      checkPageBottom();
    });
  };

  const watchPageBottom = () => {
    detachBottomWatch?.();
    if (!autoOpenAtBottom) {
      detachBottomWatch = null;
      return;
    }
    window.addEventListener('scroll', onPageScroll, { passive: true });
    window.addEventListener('resize', onPageScroll, { passive: true });
    detachBottomWatch = () => {
      window.removeEventListener('scroll', onPageScroll);
      window.removeEventListener('resize', onPageScroll);
      if (bottomScrollTick) window.cancelAnimationFrame(bottomScrollTick);
      bottomScrollTick = 0;
    };
    checkPageBottom();
  };

  const finishClose = () => {
    if (!drawer) return;
    const panel = drawer.querySelector('.offer-page__sidebar-drawer-panel');
    const backdrop = drawer.querySelector('.offer-page__sidebar-drawer-backdrop');
    panel?.classList.remove('is-dragging');
    if (panel) panel.style.transform = '';
    if (backdrop) backdrop.style.opacity = '';
    drawer.classList.remove('is-open');
    drawer.hidden = true;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-offer-sidebar-drawer-open');
    if (!document.body.classList.contains('mobile-menu-open')) {
      document.body.classList.remove('overflow-hidden');
    }
    root.classList.remove('is-sidebar-drawer-open');
    // Inline: klon w sheetcie albo prawdziwy aside wraca do treści.
    if (useCloneDrawer) {
      clearCoursePickDrawerClone();
    } else if (useRelocateDrawer) {
      restoreSidebarInline();
    }
  };

  const closeDrawer = ({ immediate = false } = {}) => {
    if (!drawer || drawer.hidden) return;
    bottomDismissed = true;
    onBeforeClose?.();
    if (immediate || prefersReducedMotion() || !drawer.classList.contains('is-open')) {
      finishClose();
      return;
    }

    const panel = drawer.querySelector('.offer-page__sidebar-drawer-panel');
    drawer.classList.remove('is-open');
    document.body.classList.remove('is-offer-sidebar-drawer-open');
    if (!document.body.classList.contains('mobile-menu-open')) {
      document.body.classList.remove('overflow-hidden');
    }

    let settled = false;
    const settle = () => {
      if (settled || !drawer || drawer.classList.contains('is-open')) return;
      settled = true;
      finishClose();
    };

    panel?.addEventListener('transitionend', (event) => {
      if (event.target === panel && event.propertyName === 'transform') settle();
    }, { once: true });
    window.setTimeout(settle, 500);
  };

  const applyMode = () => {
    if (mq.matches) {
      ensureShell();
      // Inline: karta w treści + klon w sheet. Inaczej: sidebar przenoszony do drawera.
      if (useInlineSidebar) {
        restoreSidebarInline();
        clearCoursePickDrawerClone();
        root.classList.add('has-inline-sidebar-card');
        document.body.classList.add('has-inline-sidebar-card');
        if (isCoursePickVariant) {
          root.classList.add('has-course-pick-card');
          document.body.classList.add('has-course-pick-card');
        } else {
          root.classList.remove('has-course-pick-card');
          document.body.classList.remove('has-course-pick-card');
        }
      } else {
        moveSidebarToDrawer();
        root.classList.remove('has-course-pick-card', 'has-inline-sidebar-card');
        document.body.classList.remove('has-course-pick-card', 'has-inline-sidebar-card');
      }
      bar.hidden = false;
      root.classList.add(pageClass, 'has-offer-sidebar-mobile');
      document.body.classList.add(bodyClass, 'has-offer-sidebar-mobile-bar');
      attachVisualViewportSync();
      sync();
      watchPageBottom();
      bar?.classList.remove('booking-mobile-bar--compact');
    } else {
      detachBottomWatch?.();
      detachBottomWatch = null;
      bottomDismissed = false;
      closeDrawer({ immediate: true });
      // Desktop: wróć do oryginalnej kolumny (nie slotu pod galerią).
      const sidebar = resolveSidebar();
      if (desktopSlot && sidebar && sidebar.parentElement !== desktopSlot) {
        desktopSlot.appendChild(sidebar);
      }
      sidebarSlot = desktopSlot;
      moved = false;
      clearCoursePickDrawerClone();
      root.classList.remove('has-course-pick-card', 'has-inline-sidebar-card');
      document.body.classList.remove('has-course-pick-card', 'has-inline-sidebar-card');
      detachVisualViewportSync();
      if (bar) {
        bar.classList.remove('booking-mobile-bar--compact');
        bar.hidden = true;
        bar.remove();
        bar = null;
      }
      if (drawer) {
        detachSwipe?.();
        detachSwipe = null;
        drawer.remove();
        drawer = null;
        drawerBody = null;
      }
      root.classList.remove(pageClass, 'has-offer-sidebar-mobile');
      document.body.classList.remove(bodyClass, 'has-offer-sidebar-mobile-bar');
      root.dataset.stickySummaryReady = '0';
    }
  };

  mq.addEventListener('change', applyMode);
  applyMode();

  return { sync, closeDrawer, openDrawer };
};
